import {
  type CreateSFMChildPositionV1Dto,
  type CreateSFMParentPositionV1Dto,
  type PatchSFMChildPositionV1Dto,
  type PatchSFMParentPositionV1Dto,
  type SFMChildPositionV1Dto,
  type SFMParentPositionV1Dto,
  SfmPositionsService,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type { SfmChildPositionSimple, SfmParentPositionSimple } from '../SfmPositionTypes';

export function useSfmPositions() {
  const fetchSfmPosition = async (id: string) => {
    try {
      return await SfmPositionsService.sfmParentPositionControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchSfmPositions = async () => {
    try {
      return await SfmPositionsService.sfmParentPositionControllerV1FindAllV1();
    } catch (err) {
      console.log(err);
    }
  };

  const createSfmPosition = async (data: CreateSFMParentPositionV1Dto) => {
    try {
      await SfmPositionsService.sfmParentPositionControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const createSfmChildPosition = async (parentId: string, data: CreateSFMChildPositionV1Dto) => {
    try {
      await SfmPositionsService.sfmChildPositionControllerV1CreateV1(parentId, data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const updateSfmPosition = async (id: string, data: PatchSFMParentPositionV1Dto) => {
    try {
      await SfmPositionsService.sfmParentPositionControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const updateSfmChildPosition = async (
    parentId: string,
    id: string,
    data: PatchSFMChildPositionV1Dto,
  ) => {
    try {
      await SfmPositionsService.sfmChildPositionControllerV1UpdatePartialV1(parentId, id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteSfmPosition = async (ids: string) => {
    try {
      await SfmPositionsService.sfmParentPositionControllerV1RemoveOneV1(ids);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteSfmPositions = async (ids: string[]) => {
    try {
      await SfmPositionsService.sfmParentPositionControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteSfmChildPosition = async (parentId: string, id: string) => {
    try {
      await SfmPositionsService.sfmChildPositionControllerV1RemoveOneV1(parentId, id);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteSfmChildPositions = async (parentId: string, ids: string[]) => {
    try {
      await SfmPositionsService.sfmChildPositionControllerV1RemoveManyV1(parentId, { values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  return {
    fetchSfmPosition,
    fetchSfmPositions,
    createSfmPosition,
    createSfmChildPosition,
    updateSfmPosition,
    updateSfmChildPosition,
    deleteSfmPosition,
    deleteSfmPositions,
    deleteSfmChildPosition,
    deleteSfmChildPositions,
  };
}

export function useSfmPositionsConverter() {
  const convertSfmParentPositionDtoToSfmParentPositionSimple = (
    data: SFMParentPositionV1Dto,
  ): SfmParentPositionSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, children, ...res } = data;
    return {
      ...res,
      children: convertSfmChildPositionDtoListToSfmChildPositionSimpleList(children),
    };
  };

  const convertSfmChildPositionDtoToSfmChildPositionSimple = (
    data: SFMChildPositionV1Dto,
  ): SfmChildPositionSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, ...res } = data;
    return res;
  };

  const convertSfmParentPositionDtoListToSfmParentPositionSimpleList = (
    data: SFMParentPositionV1Dto[],
  ): SfmParentPositionSimple[] => {
    return data.map(convertSfmParentPositionDtoToSfmParentPositionSimple);
  };

  const convertSfmChildPositionDtoListToSfmChildPositionSimpleList = (
    data: SFMChildPositionV1Dto[],
  ): SfmChildPositionSimple[] => {
    return data.map(convertSfmChildPositionDtoToSfmChildPositionSimple);
  };

  const convertSfmParentPositionSimpleToCreateSfmParentPositionDto = (
    data: SfmParentPositionSimple,
  ): CreateSFMParentPositionV1Dto => {
    const { version: _version, id: _id, children, ...res } = data;
    return { ...res, children: children?.map((x) => x.name) || undefined };
  };

  const convertSfmParentPositionSimpleToUpdateSfmParentPositionDto = (
    data: SfmParentPositionSimple,
  ): PatchSFMParentPositionV1Dto => {
    const { id: _id, children: _children, ...res } = data;
    return res;
  };

  const convertSfmChildPositionSimpleToUpdateSfmChildPositionDto = (
    data: SfmChildPositionSimple,
  ): PatchSFMChildPositionV1Dto => {
    const { id: _id, ...res } = data;
    return res;
  };

  return {
    convertSfmParentPositionDtoToSfmParentPositionSimple,
    convertSfmParentPositionDtoListToSfmParentPositionSimpleList,
    convertSfmParentPositionSimpleToCreateSfmParentPositionDto,
    convertSfmParentPositionSimpleToUpdateSfmParentPositionDto,
    convertSfmChildPositionSimpleToUpdateSfmChildPositionDto,
    convertSfmChildPositionDtoToSfmChildPositionSimple,
    convertSfmChildPositionDtoListToSfmChildPositionSimpleList,
  };
}
